import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { education } from "@/mocks/devData";

gsap.registerPlugin(ScrollTrigger);

const EDU_META = [
  { icon: "ri-building-4-line", accent: "#22c55e", tag: "University" },
  { icon: "ri-book-open-line", accent: "#4ade80", tag: "Course" },
  { icon: "ri-book-open-line", accent: "#4ade80", tag: "Course" },
  { icon: "ri-book-open-line", accent: "#86efac", tag: "Course" },
];

const EDU_YEARS: Record<number, string> = {
  1: "2022 — Present",
  2: "2023",
  3: "2024",
  4: "2025",
};

export default function DevEducation() {
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(headerRef.current, { opacity: 0, y: 40 }, {
        opacity: 1, y: 0, duration: 0.9, ease: "power3.out",
        scrollTrigger: { trigger: headerRef.current, start: "top 85%", once: true },
      });
      if (cardsRef.current) {
        const cards = cardsRef.current.querySelectorAll(".edu-card");
        gsap.fromTo(cards, { opacity: 0, y: 50, scale: 0.95 }, {
          opacity: 1, y: 0, scale: 1, duration: 0.7, stagger: 0.15, ease: "power3.out",
          scrollTrigger: { trigger: cardsRef.current, start: "top 80%", once: true },
        });
      }
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      id="education"
      ref={sectionRef}
      className="relative py-20 md:py-32 overflow-hidden"
      style={{
        background: `
          radial-gradient(at 20% 20%, rgba(22,163,74,0.05) 0px, transparent 50%),
          radial-gradient(at 80% 80%, rgba(22,163,74,0.04) 0px, transparent 50%),
          #000000
        `,
      }}
    >
      <div className="absolute right-[-100px] top-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(34,197,94,0.05) 0%, transparent 65%)", filter: "blur(60px)" }} />

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div ref={headerRef} className="mb-16 md:mb-20">
          <div className="flex items-center gap-3 mb-5">
            
            <span className="text-xs font-mono text-gray-600 tracking-widest uppercase">Academic Path</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
            <h2 className="text-4xl md:text-5xl font-light text-white tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Education &amp;{" "}<span className="font-black text-green-600">Learning</span>
            </h2>
            <p className="text-gray-400 text-xs font-mono">Always expanding knowledge</p>
          </div>
          <div className="mt-6 h-px" style={{ background: "rgba(255,255,255,0.06)" }} />
        </div>

        {/* Unified panel */}
        <div
          className="rounded-3xl p-6 md:p-10"
          style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)" }}
        >
          {/* Cards */}
          <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {education.map((edu, idx) => {
              const meta = EDU_META[idx] ?? EDU_META[3];
              const isActive = edu.status === "Currently Pursuing";
              const isHovered = hoveredIdx === idx;

              return (
                <div
                  key={edu.id}
                  className="edu-card relative rounded-2xl overflow-hidden cursor-default group transition-all duration-300"
                  style={{
                    background: "rgba(255,255,255,0.03)",
                    border: isHovered ? "1px solid rgba(255,255,255,0.18)" : "1px solid rgba(255,255,255,0.09)",
                    transform: isHovered ? "translateY(-4px)" : "translateY(0)",
                  }}
                  onMouseEnter={() => setHoveredIdx(idx)}
                  onMouseLeave={() => setHoveredIdx(null)}
                >
                  {/* Top color bar */}
                  <div
                    className="h-[1px] w-full transition-all duration-500"
                    style={{
                      background: isActive
                        ? `linear-gradient(90deg, ${meta.accent}, transparent)`
                        : `linear-gradient(90deg, ${meta.accent}40, transparent)`,
                      opacity: isHovered ? 1 : 0.6,
                    }}
                  />

                  <div className="p-6">
                    {/* Top row */}
                    <div className="flex items-start justify-between gap-3 mb-5">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110"
                        style={{ background: `${meta.accent}18`, border: `1px solid ${meta.accent}35` }}
                      >
                        <i className={`${meta.icon} text-xl`} style={{ color: meta.accent }} />
                      </div>

                      <div className="flex flex-col items-end gap-1.5">
                        {isActive ? (
                          <span
                            className="flex items-center gap-1.5 text-[10px] font-mono px-2.5 py-1 rounded-full"
                            style={{ background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.35)", color: "#4ade80" }}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse inline-block" />
                            ACTIVE
                          </span>
                        ) : (
                          <span
                            className="text-[10px] font-mono px-2.5 py-1 rounded-full"
                            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#9ca3af" }}
                          >
                            ✓ DONE
                          </span>
                        )}
                        <span
                          className="text-[10px] font-mono px-2 py-0.5 rounded-md"
                          style={{ background: `${meta.accent}15`, color: `${meta.accent}dd` }}
                        >
                          {meta.tag}
                        </span>
                      </div>
                    </div>

                    <h3
                      className="text-white font-semibold text-base leading-snug mb-1.5 group-hover:text-green-300 transition-colors duration-200"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      {edu.degree}
                    </h3>
                    <p className="text-gray-400 text-[13px] font-mono mb-1">{edu.institution}</p>
                    <p className="text-gray-500 text-[11px] font-mono mb-4">{EDU_YEARS[edu.id] ?? ""}</p>

                    <div className="h-px mb-4" style={{ background: "rgba(255,255,255,0.06)" }} />

                    <div className="flex items-center justify-between gap-3">
                      <div />

                      {isActive && (
                        <div className="flex items-center gap-2 flex-1 max-w-[140px]">
                          <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                            <div
                              className="h-full rounded-full transition-all duration-700"
                              style={{
                                width: isHovered ? "90%" : "80%",
                                background: "linear-gradient(90deg, #22c55e, #4ade80)",
                                boxShadow: "0 0 6px rgba(34,197,94,0.4)",
                                transitionDelay: isHovered ? "0.1s" : "0s",
                              }}
                            />
                          </div>
                          <span className="text-[10px] font-mono text-green-400/80 whitespace-nowrap">90%</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Hover glow corner */}
                  <div
                    className="absolute bottom-0 right-0 w-24 h-24 pointer-events-none rounded-full transition-opacity duration-300"
                    style={{ background: `radial-gradient(circle, ${meta.accent}18, transparent 70%)`, opacity: isHovered ? 1 : 0, transform: "translate(30%, 30%)" }}
                  />
                </div>
              );
            })}
          </div>

          {/* Terminal */}
          {/* <div
            className="mt-8 rounded-xl px-5 py-3.5 flex items-center gap-3"
            style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.08)" }}
          >
            <div className="flex gap-1 items-center flex-shrink-0">
              <span className="w-2 h-2 rounded-full bg-red-500/40 inline-block" />
              <span className="w-2 h-2 rounded-full bg-yellow-500/40 inline-block" />
              <span className="w-2 h-2 rounded-full bg-green-500/40 inline-block" />
            </div>
            <i className="ri-terminal-line text-gray-400 text-sm" />
            <span className="text-[11px] font-mono text-gray-400">
              <span className="text-green-400/70">$ </span>
              education.status = &quot;always learning&quot;;{" "}
              <span className="text-green-400/50">// never stops</span>
            </span>
          </div> */}
        </div>
      </div>
    </section>
  );
}